//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { Contract, Signer } from 'ethers'

import { ActivityEnum, RoleEnum } from '@shared/Enums'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'

import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'

import { PoolControllerAbi } from '@shared/Abis'

import { PoolOverviewCTO } from '@shared/sharedTypes'
import { poolActions } from '@react/redux/poolSlice'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'
import { otAssert } from '@shared/conversions'

export const completeRolloverNetPaymentTX =
  (props: { poolAddr: EthAddr }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const activity = ActivityEnum.COMPLETE_ROLLOVER_NET_PAYMENT
    try {
      const { webSocket, userConnection } = getState().chainState
      const { providerReadOnly } = getState().chainState.chainConfiguration
      otAssert(providerReadOnly, 'providerReadOnly is not defined')
      const { chainConfigurationName } = getState().chainState.chainConfiguration
      otAssert(userConnection, 'userConnection is not defined')
      const { roleEnum, signer } = userConnection
      assertRole(roleEnum, RoleEnum.POOL_ADMIN)
      const { poolAddr } = props
      const poolOverviewCTO = getState().poolState.poolOverviewCTOs[poolAddr] as PoolOverviewCTO

      logOT.info('completeRolloverNetPayment', poolAddr)

      completeRolloverNetPayment(
        webSocket,
        signer as Signer,
        poolOverviewCTO.poolAddr,
        poolOverviewCTO.poolControllerAddr,
        poolOverviewCTO.maturedLoanState.loanAddr,
        poolOverviewCTO as PoolOverviewCTO,
        chainConfigurationName
      )
      dispatch(poolActions.completeRolloverNetPaymentSuccess({ poolAddr }))
    } catch (e: any) {
      await dispatchError(dispatch, e, activity, 4003)
    }
  }
export const completeRolloverNetPayment = async (
  webSocket: WebSocket | undefined,
  poolAdminSigner: Signer,
  poolAddr: EthAddr,
  poolControllerAddr: EthAddr,
  priorLoanAddr: EthAddr,
  poolOverviewCTO: PoolOverviewCTO,
  chainConfigurationName: string
): Promise<void> => {
  logOT.debug('completeRolloverNetPayment')
  const uuid = txUUID()
  webSocket?.send(
    wsServerActionPoolTXRequest(ActivityEnum.POOL_ACTIVATE, poolOverviewCTO.poolAddr, uuid, chainConfigurationName)
  )

  const poolControllerContract = new Contract(poolControllerAddr, PoolControllerAbi, poolAdminSigner)
  const approveReceiptTx = await poolControllerContract.completeRolloverNetPayment(priorLoanAddr)

  webSocket?.send(
    wsServerActionPoolTX(
      ActivityEnum.POOL_ACTIVATE,
      poolAddr,
      uuid,
      approveReceiptTx.hash as TxHash,
      chainConfigurationName
    )
  )
  await approveReceiptTx.wait(1)

  for (let i = 0; i < poolOverviewCTO.redemptionLenders.length; i++) {
    await releaseRolloverRedemption(
      webSocket,
      poolAdminSigner,
      poolOverviewCTO.poolControllerAddr,
      poolOverviewCTO.redemptionLenders[i],
      poolOverviewCTO.poolAddr,
      uuid,
      chainConfigurationName
    )
  }
  logOT.debug('{ completeRolloverNetPayment')
}
export const releaseRolloverRedemption = async (
  webSocket: WebSocket | undefined,
  borrowerSigner: Signer,
  poolControllerAddr: EthAddr,
  lenderAddr: EthAddr,
  poolAddr: EthAddr,
  uuid: string,
  chainConfigurationName: string
): Promise<void> => {
  logOT.info('releaseRolloverRedemption')

  const poolControllerContract = new Contract(poolControllerAddr, PoolControllerAbi, borrowerSigner)

  const tx2 = await poolControllerContract.releaseRolloverRedemption(lenderAddr)

  webSocket?.send(
    wsServerActionPoolTX(ActivityEnum.POOL_ACTIVATE, poolAddr, uuid, tx2.hash as TxHash, chainConfigurationName)
  )
  const approveReceipt = await tx2.wait(1)

  logOT.info('{ releaseRolloverRedemption', approveReceipt)
}
